import type {
  ForgePool,
  ForgeTokenDetail,
  GameSwapPool,
  NetworkStats,
  RewardPool,
  StakeInfo,
  UserDepositInfo,
  WithdrawPosition,
} from "../features/wallet-portfolio/types";

/**
 * dapp-ui가 렌더하는 outlink의 대분류.
 * `portfolio`는 세부적으로 `origin`으로 더 나뉜다.
 */
export type OutlinkCategory = "terms" | "privacy" | "portfolio" | "send";

/**
 * `onOutlink` 콜백에 전달되는 호출 컨텍스트. `category` + `origin`으로
 * 호출측이 목적지별 분기를 할 수 있고, `portfolio-*`의 경우 해당 섹션의
 * 원시 데이터를 `payload`로 내려보낸다.
 *
 * 새로운 포트폴리오 섹션이 추가될 때는 이 union에 variant 하나만 더하면
 * 자동으로 호출측에도 타입이 좁혀져서 전달된다.
 */
export type OutlinkContext =
  | { category: "terms"; origin: "terms" }
  | { category: "privacy"; origin: "privacy" }
  | {
      category: "portfolio";
      origin: "portfolio-rewards";
      payload: {
        pool: RewardPool;
        userDeposit?: UserDepositInfo;
      };
    }
  | {
      category: "portfolio";
      origin: "portfolio-points";
      payload: {
        position: WithdrawPosition;
      };
    }
  | {
      category: "portfolio";
      origin: "portfolio-stake";
      payload: {
        stakeInfo?: StakeInfo;
        networkStats?: NetworkStats;
      };
    }
  | {
      category: "portfolio";
      origin: "portfolio-game-swap";
      payload: {
        pool: GameSwapPool;
      };
    }
  | {
      category: "portfolio";
      origin: "portfolio-forge";
      payload: {
        pool: ForgePool;
        tokenDetail?: ForgeTokenDetail;
      };
    }
  | {
      category: "send";
      origin: "send-transaction";
      payload: {
        chainId: number;
        txHash: `0x${string}`;
      };
    };

export type OutlinkOrigin = OutlinkContext["origin"];

/**
 * outlink 가로채기 콜백. 반환값 규칙:
 *
 *   - `string`      → 해당 URL로 이동(원본을 변형 가능).
 *   - `null`        → 이동 취소(창도 열리지 않음).
 *   - `undefined`   → 원본 URL로 그대로 이동 (no-op).
 *   - `Promise<...>`→ 위 값 중 하나를 resolve. 비동기 동안 사용자 제스처를
 *                     유지하기 위해 빈 창이 먼저 열린 뒤 URL이 채워진다.
 *
 * middle-click · cmd-click · ctrl-click 등 브라우저 새 탭 단축 동작은
 * 가로채지 않고 원본 `href`로 그대로 열린다.
 */
export type OnOutlink = (
  link: string,
  ctx: OutlinkContext,
) => string | null | undefined | Promise<string | null | undefined>;

/**
 * `<a target="_blank">` 클릭 이벤트를 받아서 `onOutlink` 핸들러를 실행하고
 * 최종 URL로 새 창을 연다.
 *
 * - sync 반환: `click` 이벤트 동기 스택 안에서 `window.open(url)` 을 1회만
 *   호출한다. 팝업 차단기는 사용자 제스처 동기 스택 내의 open을 허용하므로
 *   빈 창을 미리 열 필요가 없다. 빈 창을 여는 패턴은 RN WebView 처럼
 *   `window.open` 을 즉시 OS URL 오픈(`Linking.openURL`)으로 매핑하는 환경에서
 *   "Invalid URL: cannot be empty" 에러를 유발하므로, sync 경로에서는 반드시
 *   빈 창을 만들지 않는다.
 * - async 반환(Promise): resolve 이후의 `window.open` 은 팝업 차단기가
 *   막을 수 있으므로, 사용자 제스처 안에서 빈 창을 선(先)개방 해두고
 *   resolve 된 URL 을 그 창의 location 에 주입한다.
 *
 * 반환값 없음 — 이벤트에 대한 모든 부수효과를 처리.
 */
export function handleOutlinkClick(
  e: React.MouseEvent<HTMLAnchorElement>,
  url: string,
  ctx: OutlinkContext,
  onOutlink: OnOutlink | undefined,
): void {
  // 핸들러가 없으면 anchor 기본 동작을 그대로 둔다.
  if (!onOutlink) return;

  // middle-click / modified click은 브라우저 기본 탭 열기 UX를 유지한다.
  // 기본 동작만 가로채는 정책.
  if (
    e.button !== 0 ||
    e.metaKey ||
    e.ctrlKey ||
    e.shiftKey ||
    e.altKey ||
    e.defaultPrevented
  ) {
    return;
  }

  e.preventDefault();

  let maybe: string | null | undefined | Promise<string | null | undefined>;
  try {
    maybe = onOutlink(url, ctx);
  } catch (err) {
    console.error("[dapp-ui] onOutlink threw:", err);
    return;
  }

  const openInNewTab = (target: string) => {
    if (typeof window === "undefined") return;
    window.open(target, "_blank", "noopener,noreferrer");
  };

  const isPromise = (
    v: unknown,
  ): v is Promise<string | null | undefined> =>
    !!v &&
    (typeof v === "object" || typeof v === "function") &&
    typeof (v as { then?: unknown }).then === "function";

  if (isPromise(maybe)) {
    // Async 경로: popup blocker 회피를 위해 빈 창 선-개방 후 URL 주입.
    const win =
      typeof window !== "undefined"
        ? window.open("", "_blank", "noopener,noreferrer")
        : null;

    const finalize = (result: string | null | undefined) => {
      if (result === null) {
        win?.close();
        return;
      }
      const target = result ?? url;
      if (win) {
        try {
          win.location.href = target;
        } catch {
          // 일부 브라우저에서 noopener로 연 창은 location 쓰기가 막힐 수 있음 — 폴백.
          win.close();
          openInNewTab(target);
        }
      } else {
        openInNewTab(target);
      }
    };

    maybe.then(finalize, (err) => {
      console.error("[dapp-ui] onOutlink rejected:", err);
      win?.close();
    });
    return;
  }

  // Sync 경로: 빈 창을 미리 열지 않고, 반환된 URL 로 1회만 window.open.
  const result = maybe as string | null | undefined;
  if (result === null) return; // 이동 취소
  const target = result ?? url;
  openInNewTab(target);
}
